/**
 * Classifica gli errori intercettati dall'ErrorBoundary per mostrare
 * all'utente un messaggio comprensibile invece dello stack tecnico.
 *
 * Durante un deploy si verificano due scenari tipici:
 * - i chunk JS della vecchia build non esistono piu' e l'import dinamico
 *   fallisce (nginx risponde con index.html al posto del file .js);
 * - il backend non e' ancora disponibile e le chiamate API tornano
 *   404/502/503/504 oppure falliscono a livello di rete.
 */

export const APP_ERROR_KIND = {
  APP_UPDATED: "appUpdated",
  SERVICE_UNAVAILABLE: "serviceUnavailable",
  GENERIC: "generic",
};

// Messaggi generati dai browser quando un import dinamico non e' piu' raggiungibile
const CHUNK_ERROR_PATTERNS = [
  /dynamically imported module/i,
  /loading chunk \S+ failed/i,
  /importing a module script failed/i,
  /error loading dynamically imported module/i,
  /unknown variable dynamic import/i,
  /is not a valid javascript mime type/i,
  /expected a javascript(-or-wasm)? module script/i,
];

// Stati restituiti dal gateway mentre i container del backend si riavviano
const UNAVAILABLE_STATUSES = [404, 502, 503, 504];

const getErrorMessage = (error) => {
  if (!error) return "";
  if (typeof error === "string") return error;
  return `${error.name || ""} ${error.message || ""}`.trim();
};

/** Errore causato da un chunk JS non piu' presente sul server (nuova build). */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;
  const message = getErrorMessage(error);
  if (CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))) return true;
  return error.cause ? isChunkLoadError(error.cause) : false;
};

/** Errore causato dal backend irraggiungibile o in fase di riavvio. */
export const isServiceUnavailableError = (error) => {
  if (!error) return false;
  const status = error.response?.status ?? error.status;
  if (UNAVAILABLE_STATUSES.includes(status)) return true;
  // Axios: richiesta partita ma nessuna risposta ricevuta (backend down)
  if (error.isAxiosError && !error.response) return true;
  if (error.code === "ERR_NETWORK" || error.code === "ECONNABORTED") return true;
  const message = getErrorMessage(error);
  if (/network error|failed to fetch|load failed/i.test(message)) return true;
  return error.cause ? isServiceUnavailableError(error.cause) : false;
};

/** Restituisce la categoria dell'errore da usare per il messaggio utente. */
export const classifyAppError = (error) => {
  if (isChunkLoadError(error)) return APP_ERROR_KIND.APP_UPDATED;
  if (isServiceUnavailableError(error)) return APP_ERROR_KIND.SERVICE_UNAVAILABLE;
  return APP_ERROR_KIND.GENERIC;
};
